import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Menu } from "lucide-react";
import LanguageSelector from "./LanguageSelector";

interface MenuItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
}

interface MobileNavProps {
  productItems: MenuItem[];
  solutionsItems: MenuItem[];
  resourcesItems: MenuItem[];
  companyItems: MenuItem[];
  onBookDemo: () => void;
}

const MobileNav = ({
  productItems,
  solutionsItems,
  resourcesItems,
  companyItems,
  onBookDemo,
}: MobileNavProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleItemClick = () => {
    setOpen(false);
  };

  const handleBookDemo = () => {
    setOpen(false);
    onBookDemo();
  };

  const renderMenuItems = (items: MenuItem[]) => (
    <div className="space-y-1">
      {items.map((item) => (
        <a
          key={item.title}
          href={item.href}
          onClick={handleItemClick}
          className="flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-accent"
        >
          <div className="mt-0.5 text-muted-foreground">{item.icon}</div>
          <div>
            <div className="text-sm font-medium">{item.title}</div>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {item.description}
            </p>
          </div>
        </a>
      ))}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden">
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[400px] overflow-y-auto">
        <div className="flex flex-col h-full">
          {/* Language Selector */}
          <div className="flex justify-end py-2 border-b border-border mb-4">
            <LanguageSelector />
          </div>

          {/* Navigation Accordion */}
          <div className="flex-1">
            <Accordion type="single" collapsible className="w-full">
              {/* Product */}
              <AccordionItem value="product">
                <AccordionTrigger className="text-base font-medium">
                  {t('marketingHeader.product')}
                </AccordionTrigger>
                <AccordionContent>
                  {renderMenuItems(productItems)}
                </AccordionContent>
              </AccordionItem>

              {/* Solutions */}
              <AccordionItem value="solutions">
                <AccordionTrigger className="text-base font-medium">
                  {t('marketingHeader.solutions')}
                </AccordionTrigger>
                <AccordionContent>
                  {renderMenuItems(solutionsItems)}
                </AccordionContent>
              </AccordionItem>

              {/* Resources */}
              <AccordionItem value="resources">
                <AccordionTrigger className="text-base font-medium">
                  {t('marketingHeader.resources')}
                </AccordionTrigger>
                <AccordionContent>
                  {renderMenuItems(resourcesItems)}
                </AccordionContent>
              </AccordionItem>

              {/* Company */}
              <AccordionItem value="company">
                <AccordionTrigger className="text-base font-medium">
                  {t('marketingHeader.company')}
                </AccordionTrigger>
                <AccordionContent>
                  {renderMenuItems(companyItems)}
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Pricing Link */}
            <a
              href="#pricing"
              onClick={handleItemClick}
              className="block py-4 text-base font-medium border-b border-border hover:text-primary transition-colors"
            >
              {t('marketingHeader.pricing')}
            </a>
          </div>

          {/* CTA Buttons */}
          <div className="mt-auto pt-6 space-y-3 border-t border-border">
            <Button variant="outline" asChild className="w-full">
              <Link to="/auth" onClick={handleItemClick}>
                {t('marketingHeader.login')}
              </Link>
            </Button>
            <Button
              onClick={handleBookDemo}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
            >
              {t('marketingHeader.bookDemo')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNav;
